import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, Menu, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/learning", label: "Learning" },
  { to: "/recruitment", label: "Recruitment" },
  { to: "/payroll", label: "Payroll" },
  { to: "/careers", label: "Open Positions" },
  { to: "/contact", label: "Contact" },
];

export const SiteHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-offwhite/85 border-b border-navy/10" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="brand-logo">
          <img src="/ravemora-lockup.png" alt="Ravemora Global" className="h-5 md:h-8 lg:h-9 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `label-eyebrow transition-colors ${isActive ? "text-gold" : "text-navy hover:text-gold"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          {user ? (
            <>
              <button
                onClick={() => navigate("/dashboard")}
                className="btn-outline !px-3 !py-2 !text-[11px] md:!px-6 md:!py-3 md:!text-xs whitespace-nowrap inline-flex items-center gap-1.5"
                data-testid="header-dashboard-btn"
                aria-label="Dashboard"
              >
                <LayoutDashboard size={14} /> <span className="hidden sm:inline">Dashboard</span>
              </button>
              <button
                onClick={logout}
                className="btn-primary !px-3 !py-2 !text-[11px] md:!px-6 md:!py-3 md:!text-xs whitespace-nowrap inline-flex items-center gap-1.5"
                data-testid="header-logout-btn"
                aria-label="Log out"
              >
                <LogOut size={14} /> <span className="hidden sm:inline">Log out</span>
              </button>
            </>
          ) : (
            <button
              className="btn-primary !px-3 !py-2 !text-[10px] md:!px-5 md:!py-[10px] md:!text-[11px] whitespace-nowrap inline-flex items-center"
              onClick={() => navigate("/login")}
              data-testid="header-login-btn"
            >
              Sign in
            </button>
          )}
          {/* Mobile / tablet hamburger */}
          <button
            className="lg:hidden p-2 -mr-2 text-navy hover:text-gold transition-colors"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-navy/10 bg-offwhite/95 backdrop-blur-xl" data-testid="mobile-menu-panel">
          <nav className="max-w-[1400px] mx-auto px-6 py-4 flex flex-col divide-y divide-navy/10">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={closeMobile}
                data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `label-eyebrow py-4 flex items-center justify-between transition-colors ${isActive ? "text-gold" : "text-navy hover:text-gold"}`
                }
              >
                <span>{n.label}</span>
                <span className="text-navy/30">→</span>
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export const SiteFooter = () => (
  <footer className="relative bg-navy text-offwhite grain overflow-hidden" data-testid="site-footer">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-4 gap-12 relative z-10">
      <div className="md:col-span-2">
        <div className="serif-display text-4xl md:text-5xl font-light leading-none">Ravemora Global<span className="text-gold">.</span></div>
        <div className="label-eyebrow text-gold mt-4">Human Capital Transformation</div>
        <p className="mt-4 max-w-md text-offwhite/70 leading-relaxed">
          Developing people. Connecting talent. Building stronger workforces.
        </p>
      </div>
      <div>
        <div className="label-eyebrow text-gold">Company</div>
        <ul className="mt-4 space-y-2 text-offwhite/80">
          <li><Link to="/">Home</Link></li>
          <li><Link to="/learning">Learning</Link></li>
          <li><Link to="/recruitment">Recruitment</Link></li>
          <li><Link to="/payroll">Payroll</Link></li>
          <li><Link to="/careers">Open Positions</Link></li>
          <li><Link to="/contact">Contact</Link></li>
        </ul>
      </div>
      <div>
        <div className="label-eyebrow text-gold">Contact</div>
        <ul className="mt-4 space-y-2 text-offwhite/80">
          <li>Bengaluru, India</li>
          <li>info@ravemora.com</li>
          <li>+91 74835 00550</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10 py-6 px-6 lg:px-10 max-w-[1400px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-offwhite/60">
      <div>© {new Date().getFullYear()} Ravemora Global. All rights reserved.</div>
      <div className="flex items-center gap-6">
        <Link to="/privacy" className="hover:text-gold transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
        <Link to="/terms" className="hover:text-gold transition-colors" data-testid="footer-terms">Terms &amp; Conditions</Link>
      </div>
    </div>
  </footer>
);

